/**
 * "Myyjien ilmoitukset" — public seller-submission form (#/myynti-ilmoitus).
 *
 * Brokers reach this page via a shared link (optionally `?ref=toimisto-x`)
 * and fill in the sale details themselves. No login required — the
 * submission lands in the admin "Myyjien ilmoitukset" queue as 'pending'
 * and only becomes a Metsätilat-listing row once an admin approves it
 * (see backend/internal/services/smt_seller_submission_service.go).
 *
 * The form is generated from `FIELD_GROUPS` below so every payload field
 * gets a consistent input without hand-writing ~70 near-identical <input>
 * blocks.
 */
import { submitSellerSubmission, type SellerSubmissionCreateInput } from '../api/seller-submissions.ts';
import { APIRequestError } from '../api/client.ts';
import { t } from '../i18n/index.ts';

type FieldType = 'text' | 'number' | 'checkbox' | 'textarea';

interface FieldDef {
  key: keyof SellerSubmissionCreateInput;
  labelKey: string;
  type: FieldType;
  required?: boolean;
}

interface FieldGroup {
  titleKey: string;
  fields: FieldDef[];
}

const FIELD_GROUPS: FieldGroup[] = [
  {
    titleKey: 'ssf_g_perustiedot',
    fields: [
      { key: 'property_id', labelKey: 'ssf_property_id', type: 'text', required: true },
      { key: 'name', labelKey: 'ssf_name', type: 'text', required: true },
      { key: 'municipality', labelKey: 'ssf_municipality', type: 'text', required: true },
      { key: 'region', labelKey: 'ssf_region', type: 'text' },
      { key: 'area', labelKey: 'ssf_area', type: 'number' },
      { key: 'forest_area', labelKey: 'ssf_forest_area', type: 'number' },
      { key: 'forest_type', labelKey: 'ssf_forest_type', type: 'text' },
    ],
  },
  {
    titleKey: 'ssf_g_hintatiedot',
    fields: [
      { key: 'price', labelKey: 'ssf_price', type: 'number' },
      { key: 'pelto_arvo', labelKey: 'ssf_pelto_arvo', type: 'number' },
      { key: 'tontti_rakennus_arvo', labelKey: 'ssf_tontti_rakennus_arvo', type: 'number' },
      { key: 'muu_arvo', labelKey: 'ssf_muu_arvo', type: 'number' },
      { key: 'wood_value', labelKey: 'ssf_wood_value', type: 'number' },
      { key: 'sum_value', labelKey: 'ssf_sum_value', type: 'number' },
    ],
  },
  {
    titleKey: 'ssf_g_lisatiedot',
    fields: [
      { key: 'has_buildings', labelKey: 'ssf_has_buildings', type: 'checkbox' },
      { key: 'has_zoning', labelKey: 'ssf_has_zoning', type: 'checkbox' },
      { key: 'zoning_type', labelKey: 'ssf_zoning_type', type: 'text' },
      { key: 'has_protected_area', labelKey: 'ssf_has_protected_area', type: 'checkbox' },
      { key: 'has_forest_damage', labelKey: 'ssf_has_forest_damage', type: 'checkbox' },
      { key: 'has_road_access', labelKey: 'ssf_has_road_access', type: 'checkbox' },
      { key: 'hunting_agreement', labelKey: 'ssf_hunting_agreement', type: 'checkbox' },
      { key: 'encumbrances', labelKey: 'ssf_encumbrances', type: 'text' },
      { key: 'has_pelto', labelKey: 'ssf_has_pelto', type: 'checkbox' },
      { key: 'has_oma_ranta', labelKey: 'ssf_has_oma_ranta', type: 'checkbox' },
    ],
  },
  {
    titleKey: 'ssf_g_tilavuus',
    fields: [
      { key: 'manty_kuitupuu_m3', labelKey: 'ssf_manty_kuitupuu', type: 'number' },
      { key: 'manty_tukkipuu_m3', labelKey: 'ssf_manty_tukkipuu', type: 'number' },
      { key: 'kuusi_kuitupuu_m3', labelKey: 'ssf_kuusi_kuitupuu', type: 'number' },
      { key: 'kuusi_tukkipuu_m3', labelKey: 'ssf_kuusi_tukkipuu', type: 'number' },
      { key: 'koivu_kuitupuu_m3', labelKey: 'ssf_koivu_kuitupuu', type: 'number' },
      { key: 'koivu_tukkipuu_m3', labelKey: 'ssf_koivu_tukkipuu', type: 'number' },
      { key: 'hieskoivu_kuitupuu_m3', labelKey: 'ssf_hieskoivu_kuitupuu', type: 'number' },
      { key: 'hieskoivu_tukkipuu_m3', labelKey: 'ssf_hieskoivu_tukkipuu', type: 'number' },
      { key: 'muu_lehtipuu_m3', labelKey: 'ssf_muu_lehtipuu', type: 'number' },
      { key: 'haapa_kuitupuu_m3', labelKey: 'ssf_haapa_kuitupuu', type: 'number' },
      { key: 'muu_kuitupuu_m3', labelKey: 'ssf_muu_kuitupuu', type: 'number' },
    ],
  },
  {
    titleKey: 'ssf_g_maapohja',
    fields: [
      { key: 'metsamaa_ha', labelKey: 'ssf_metsamaa', type: 'number' },
      { key: 'kitumaa_ha', labelKey: 'ssf_kitumaa', type: 'number' },
      { key: 'joutomaa_ha', labelKey: 'ssf_joutomaa', type: 'number' },
      { key: 'pelto_ha', labelKey: 'ssf_pelto', type: 'number' },
      { key: 'maatalousmaa_ha', labelKey: 'ssf_maatalousmaa', type: 'number' },
      { key: 'tontti_ha', labelKey: 'ssf_tontti', type: 'number' },
      { key: 'vesisto_ha', labelKey: 'ssf_vesisto', type: 'number' },
      { key: 'muu_maa_ha', labelKey: 'ssf_muu_maa', type: 'number' },
    ],
  },
  {
    titleKey: 'ssf_g_kasvupaikat',
    fields: [
      { key: 'kangas_ha', labelKey: 'ssf_kangas', type: 'number' },
      { key: 'korpi_ha', labelKey: 'ssf_korpi', type: 'number' },
      { key: 'rame_ha', labelKey: 'ssf_rame', type: 'number' },
      { key: 'neva_ha', labelKey: 'ssf_neva', type: 'number' },
      { key: 'kasvupaikka_ei_tiedossa_ha', labelKey: 'ssf_ei_tiedossa', type: 'number' },
    ],
  },
  {
    titleKey: 'ssf_g_kehitysluokat',
    fields: [
      { key: 'aukea_ha', labelKey: 'ssf_aukea', type: 'number' },
      { key: 'taimikko_alle_ha', labelKey: 'ssf_taimikko_alle', type: 'number' },
      { key: 'taimikko_yli_ha', labelKey: 'ssf_taimikko_yli', type: 'number' },
      { key: 'nuori_kasvatusmetsikko_ha', labelKey: 'ssf_nuori_kasvatus', type: 'number' },
      { key: 'varttunut_kasvatusmetsikko_ha', labelKey: 'ssf_varttunut_kasvatus', type: 'number' },
      { key: 'uudistuskypsa_ha', labelKey: 'ssf_uudistuskypsa', type: 'number' },
      { key: 'siemenpuu_ha', labelKey: 'ssf_siemenpuu', type: 'number' },
      { key: 'suojuspuu_ha', labelKey: 'ssf_suojuspuu', type: 'number' },
      { key: 'eri_ikaisrakenteinen_ha', labelKey: 'ssf_eri_ikaisrakenteinen', type: 'number' },
      { key: 'ylispuustoinen_taimikko_ha', labelKey: 'ssf_ylispuustoinen', type: 'number' },
      { key: 'kehitysluokka_ei_tiedossa_ha', labelKey: 'ssf_ei_tiedossa', type: 'number' },
    ],
  },
  {
    titleKey: 'ssf_g_ravinteisuus',
    fields: [
      { key: 'lehto_ha', labelKey: 'ssf_lehto', type: 'number' },
      { key: 'letto_ha', labelKey: 'ssf_letto', type: 'number' },
      { key: 'lehtomainen_ha', labelKey: 'ssf_lehtomainen', type: 'number' },
      { key: 'tuore_kangas_ha', labelKey: 'ssf_tuore_kangas', type: 'number' },
      { key: 'kuivahko_kangas_ha', labelKey: 'ssf_kuivahko_kangas', type: 'number' },
      { key: 'kuiva_kangas_ha', labelKey: 'ssf_kuiva_kangas', type: 'number' },
      { key: 'karukkokangas_ha', labelKey: 'ssf_karukkokangas', type: 'number' },
      { key: 'kallio_hietikko_ha', labelKey: 'ssf_kallio_hietikko', type: 'number' },
      { key: 'ravinteisuus_ei_tiedossa_ha', labelKey: 'ssf_ei_tiedossa', type: 'number' },
    ],
  },
  {
    titleKey: 'ssf_g_valittaja',
    fields: [
      { key: 'broker_name', labelKey: 'ssf_broker_name', type: 'text', required: true },
      { key: 'brokerage', labelKey: 'ssf_brokerage', type: 'text' },
      { key: 'listing_url', labelKey: 'ssf_listing_url', type: 'text' },
    ],
  },
  {
    titleKey: 'ssf_g_aikataulu',
    fields: [
      { key: 'schedule', labelKey: 'ssf_schedule', type: 'text' },
      { key: 'description', labelKey: 'ssf_description', type: 'textarea' },
    ],
  },
];

function inputId(key: string): string {
  return `ssf_${key}`;
}

function renderField(f: FieldDef): string {
  const id = inputId(f.key);
  if (f.type === 'checkbox') {
    return `
      <div class="login-field ssf-checkbox-field">
        <label class="login-label" for="${id}">
          <input type="checkbox" id="${id}" data-key="${f.key}" />
          ${escapeHtml(t(f.labelKey))}
        </label>
      </div>`;
  }
  if (f.type === 'textarea') {
    return `
      <div class="login-field">
        <label class="login-label" for="${id}">${escapeHtml(t(f.labelKey))}</label>
        <textarea id="${id}" class="feedback-textarea" data-key="${f.key}" rows="4"></textarea>
      </div>`;
  }
  return `
    <div class="login-field">
      <label class="login-label" for="${id}">${escapeHtml(t(f.labelKey))}${f.required ? ' *' : ''}</label>
      <input
        type="${f.type === 'number' ? 'number' : 'text'}"
        id="${id}"
        class="login-input"
        data-key="${f.key}"
        ${f.type === 'number' ? 'step="any"' : ''}
        ${f.required ? 'required' : ''}
      />
    </div>`;
}

export async function renderSellerSubmissionForm(root: HTMLElement): Promise<void> {
  const refToken = new URLSearchParams(window.location.hash.split('?')[1] || '').get('ref') || '';

  root.innerHTML = `
    <div class="simple-page ssf-page">
      <h1 class="simple-page-title">${escapeHtml(t('seller_submission_title'))}</h1>
      <div class="simple-page-sub">${escapeHtml(t('seller_submission_sub'))}</div>
      <form id="ssfForm" class="ssf-form" novalidate>
        ${FIELD_GROUPS.map(
          (g) => `
          <fieldset class="ssf-group">
            <legend class="ssf-group-title">${escapeHtml(t(g.titleKey))}</legend>
            <div class="ssf-group-fields">${g.fields.map(renderField).join('')}</div>
          </fieldset>`,
        ).join('')}
        <div id="ssfMsg" class="modal-msg" style="display:none"></div>
        <button type="submit" class="login-btn" id="ssfSubmit">${escapeHtml(t('seller_submission_submit'))}</button>
      </form>
    </div>
  `;

  const form = root.querySelector<HTMLFormElement>('#ssfForm')!;
  const msg = root.querySelector<HTMLDivElement>('#ssfMsg')!;
  const submitBtn = root.querySelector<HTMLButtonElement>('#ssfSubmit')!;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    msg.style.display = 'none';
    msg.classList.remove('err');

    const payload = collectPayload(form);
    if (!payload.property_id || !payload.name || !payload.municipality || !payload.broker_name) {
      msg.textContent = t('seller_submission_err_required');
      msg.classList.add('err');
      msg.style.display = 'block';
      return;
    }

    submitBtn.disabled = true;
    const original = submitBtn.textContent;
    submitBtn.textContent = t('feedback_submitting');
    try {
      await submitSellerSubmission({ ...payload, ref_token: refToken } as SellerSubmissionCreateInput);
      form.reset();
      msg.textContent = t('seller_submission_success');
      msg.classList.remove('err');
      msg.style.display = 'block';
    } catch (e) {
      msg.textContent = formatErr(e as Error);
      msg.classList.add('err');
      msg.style.display = 'block';
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = original;
    }
  });
}

function collectPayload(form: HTMLFormElement): Partial<SellerSubmissionCreateInput> {
  const out: Record<string, string | number | boolean | undefined> = {};
  form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-key]').forEach((el) => {
    const key = el.dataset.key!;
    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      out[key] = el.checked;
      return;
    }
    if (el instanceof HTMLInputElement && el.type === 'number') {
      out[key] = el.value === '' ? undefined : Number(el.value);
      return;
    }
    out[key] = el.value.trim() === '' ? undefined : el.value.trim();
  });
  return out as Partial<SellerSubmissionCreateInput>;
}

function formatErr(e: Error): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  return e.message;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
